import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { LoaderIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import BillingAPIService from "@/services/billing-api";

interface SubscriptionData {
  email: string;
  password: string;
  billingTierId: number;
  billingPeriod: string;
  affiliateCode?: string;
  subscription_type?: "parent" | "contractor";
}

const SubscriptionComplete = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [error, setError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const processingPromise = useRef<Promise<void> | null>(null);

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  useEffect(() => {
    const completeSubscription = async (attempt: number = 0): Promise<void> => {
      try {
        const clientSecret = searchParams.get("setup_intent");
        if (!clientSecret) throw new Error("No setup intent found");

        const encodedData = searchParams.get("data");
        if (!encodedData) throw new Error("No subscription data found");

        const subscriptionData: SubscriptionData = JSON.parse(
          atob(encodedData),
        );

        const result = await BillingAPIService.createSubscription({
          billingTierId: subscriptionData.billingTierId,
          billingPeriod: subscriptionData.billingPeriod as "monthly" | "annual",
          affiliateCode: subscriptionData.affiliateCode,
          stripeIntentId: clientSecret,
          email: subscriptionData.email,
          subscription_type: subscriptionData.subscription_type,
        });

        if (!result.subscription) {
          throw new Error("Failed to create subscription");
        }

        // Success! Navigate to dashboard
        navigate("/dashboard", { replace: true });
      } catch (error: any) {
        const isUserNotFoundError = error.message?.includes("does not exist") || 
                                   error.status === 404;
        
        if (isUserNotFoundError && attempt < MAX_RETRIES) {
          // User account might not be ready yet, retry after a delay
          console.log(`User not found, retrying in ${RETRY_DELAY}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
          setRetryCount(attempt + 1);
          
          setTimeout(() => {
            completeSubscription(attempt + 1);
          }, RETRY_DELAY);
          return;
        }
        
        // Either not a user not found error, or we've exhausted retries
        let errorMessage = "Failed to complete subscription";
        
        if (isUserNotFoundError) {
          errorMessage = "Account creation is taking longer than expected. Please try refreshing the page or contact support if the issue persists.";
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setError(errorMessage);
        setIsProcessing(false);
        
        toast({
          title: "Subscription Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    };

    // Only start a new processing attempt if there isn't one in progress
    if (!processingPromise.current) {
      processingPromise.current = completeSubscription();
    }

    // Cleanup function to handle component unmounting
    return () => {
      processingPromise.current = null;
    };
  }, [searchParams, navigate, toast]);

  if (isProcessing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-6">
            <LoaderIcon className="h-8 w-8 animate-spin mb-4" />
            <p className="text-lg font-medium">
              Completing your subscription...
            </p>
            {retryCount > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Retrying... (attempt {retryCount}/{MAX_RETRIES})
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default SubscriptionComplete;
